import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader, Badge, Button } from '@/components/ui/primitives';
import { ErrorState, LoadingState, EmptyState } from '@/components/DataState';
import { getTicket, replyToTicket, changeTicketStatus } from '@/data/support';
import {
  authorLabel,
  isUnprovisioned,
  nextStatuses,
  STATUS_LABEL,
  STATUS_TONE,
  isTicketStatus,
  type TicketStatus,
} from '@/data/support-model';

/* /sales/support/:id — one ticket, its thread, and a staff reply box (SCAFFOLD).
 * Renders the dark-scaffold 503 as a calm panel. Reply + status transition post
 * through the workspace-scoped, CSRF-guarded data layer. */
export function SupportTicketPage() {
  const { id } = useParams<{ id: string }>();
  const ticketId = Number(id);
  const qc = useQueryClient();
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['support', 'ticket', ticketId],
    queryFn: () => getTicket(ticketId),
    retry: false,
    enabled: Number.isFinite(ticketId) && ticketId > 0,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['support', 'ticket', ticketId] });
    void qc.invalidateQueries({ queryKey: ['support', 'tickets'] });
  };

  const replyMut = useMutation({
    mutationFn: () => replyToTicket(ticketId, reply, internal),
    onSuccess: () => {
      setReply('');
      setInternal(false);
      invalidate();
    },
  });

  const statusMut = useMutation({
    mutationFn: (to: TicketStatus) => changeTicketStatus(ticketId, to),
    onSuccess: invalidate,
  });

  if (isPending) return <LoadingState rows={6} />;
  if (error) {
    return isUnprovisioned(error) ? (
      <EmptyState
        title="Support isn’t switched on yet"
        teach="This ticket view is ready, but the Support tables haven’t been provisioned. Once the owner applies the approved migration, it will load automatically."
      />
    ) : (
      <ErrorState error={error} onRetry={() => void refetch()} />
    );
  }

  const ticket = data.ticket;
  const status = isTicketStatus(ticket.status) ? ticket.status : 'open';

  return (
    <div>
      <PageHeader
        title={ticket.subject || '(no subject)'}
        subtitle={`Ticket #${ticket.id} · ${ticket.partner_ref || 'no partner'} · opened ${ticket.created_at || '—'}`}
        actions={<Badge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Badge>}
      />

      <div className="mb-4">
        <Link to="/sales/support" className="text-sm text-text-tertiary hover:underline">
          ← Back to Support inbox
        </Link>
      </div>

      {/* Status transitions */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-text-tertiary">Move to:</span>
        {nextStatuses(status).map((to) => (
          <Button
            key={to}
            variant="secondary"
            disabled={statusMut.isPending}
            onClick={() => statusMut.mutate(to)}
          >
            {STATUS_LABEL[to]}
          </Button>
        ))}
      </div>

      {/* Thread */}
      <div className="space-y-3">
        {data.messages.length === 0 ? (
          <p className="text-sm text-text-tertiary">No messages on this ticket yet.</p>
        ) : (
          data.messages.map((m) => (
            <div
              key={m.id}
              className={
                'rounded-lg border px-4 py-3 ' +
                (m.is_internal
                  ? 'border-warning/30 bg-warning/5'
                  : m.author_kind === 'staff'
                    ? 'border-border-subtle bg-inset/40'
                    : 'border-border-subtle bg-surface')
              }
            >
              <div className="mb-1 flex items-center gap-2 text-xs text-text-tertiary">
                <span className="font-medium text-text-secondary">{authorLabel(m)}</span>
                {m.is_internal && <Badge tone="warning">Internal</Badge>}
                <span>{m.created_at}</span>
              </div>
              <p className="whitespace-pre-wrap text-sm text-text-primary">{m.body}</p>
            </div>
          ))
        )}
      </div>

      {/* Reply box */}
      <div className="mt-6 rounded-lg border border-border-subtle bg-surface p-4">
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={4}
          placeholder="Write a reply…"
          className="w-full rounded-md border border-border-default bg-canvas px-3 py-2 text-sm text-text-primary"
        />
        <div className="mt-3 flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} />
            Internal note (not shown to the customer)
          </label>
          <Button
            variant="primary"
            disabled={reply.trim() === '' || replyMut.isPending}
            onClick={() => replyMut.mutate()}
          >
            {replyMut.isPending ? 'Sending…' : internal ? 'Add internal note' : 'Send reply'}
          </Button>
        </div>
        {replyMut.error && !isUnprovisioned(replyMut.error) && (
          <p className="mt-2 text-xs text-danger">Couldn’t post that reply. Try again.</p>
        )}
      </div>
    </div>
  );
}
